import { action, computed, makeAutoObservable, makeObservable, observable, override, runInAction } from 'mobx'

// evertel
import { APIDataDocument, APIDataMedia, APIDataDocumentACL, APIDataAny, APIDataDocumentSchemaRevision } from '@evertel/types'
import { injectable, inject, decorate } from '@evertel/di'
import { Api } from '@evertel/api'
import { MediaUtils } from '../utils'
import { DocumentStore, DocumentSchemaStore, DocumentSchemaRevisionStore } from '../store'
import { normalize, validate, hasErrors, countErrors } from '@evertel/schema-parser'
import { DocumentViewController } from './DocumentViewController'

type Platform = 'web' | 'mobile'

class EverdocMediaUplaodErrorEdit extends Error {
    documentId: number

    constructor(documentId, args) {
        super(args)
        this.name = 'EverdocMediaUploadError'
        this.documentId = documentId
    }
}

class DocumentEditController extends DocumentViewController {
    data: Record<string, any> = {}
    media: APIDataMedia[] = []
    ACL: APIDataDocumentACL[] = []
    schemaMedia: APIDataMedia[] = []
    platform: Platform = undefined
    errors?: Record<string, any> = null

    constructor(
        documentStore: DocumentStore,
        documentSchemaStore: DocumentSchemaStore,
        documentSchemaRevisionStore: DocumentSchemaRevisionStore,
        api: Api,
        private mediaUtils: MediaUtils
    ) {
        super(documentStore, documentSchemaStore, documentSchemaRevisionStore, api)
        //MUST be makeObservable, makeAutoObservable breaks extended classes
        makeObservable(this, {
            data: observable,
            media: observable,
            ACL: observable,
            schemaMedia: observable,
            errors: observable,
            platform: observable,
            updateData: action,
            update: action,
            validateBeforeUpdate: action,
            setName: action,
            errorsCount: computed
        })
    }

    protected getDefaultIncludes() {
        return [
            { relation: 'media' },
            { relation: 'ACL' },
            { relation: 'documentSchemaRevision' },
            {
                relation: 'documentSchema',
                scope: {
                    include: 'media'
                }
            }
        ]
    }

    async load(documentId: number, platform: Platform): Promise<APIDataDocument | undefined> {
        if (!documentId) throw new Error('Missing a document ID! (error: RN.DEC-1')

        this.platform = platform
        return super.load(documentId)
    }

    protected processLoadedDocument(document: APIDataDocument) {
        super.processLoadedDocument(document)

        runInAction(() => {
            this.data = document?.data as APIDataAny
            this.media = document?.media ?? []
            this.ACL = document?.ACL ?? []
        })
    }

    updateData = (data: Record<string, any>) => {
        // called from views to hold the data as they edit the document (state)
        runInAction(() => {
            this.data = data
        })
    }

    update = async (notify: { isUrgent: boolean } | null): Promise<APIDataDocument | undefined> => {
        // normalize schema
        const schema = normalize(this.documentSchema?.schema)

        //console.log('RAW', toJS(this.documentSchema?.schema))
        //console.log('NORMALIZED', toJS(schema))

        // this will throw if fail
        this.validateBeforeUpdate()

        // process any media first
        const processDocumentMediaData = await this.mediaUtils.processDocumentMedia(
            this.platform,
            this.data,
            this.documentId,
            schema['layouts']['full']
        )

        // post new data
        const updatedDocument = await this.api.Routes.Document.putById(this.documentId, {
            name: this.name,
            data: processDocumentMediaData
        })

        if (this.mediaUtils.errors.length) {
            const messages = this.mediaUtils.errors.map(e => e.message).join(', \n')
            const count = this.mediaUtils.errors.length
            this.mediaUtils.clearErrors()
            throw new EverdocMediaUplaodErrorEdit(this.documentId, `${count} uploads failed: ${messages}`)
        }

        // if save & notify...
        if (notify) this.notify(notify.isUrgent)

        runInAction(() => {
            // update the DocumentStore data
            this.documentStore.update(updatedDocument)
        })

        // return the updated document model
        return this.documentStore.findById(this.documentId)
    }

    validateBeforeUpdate = () => {
        this.errors = null

        // normalize schema
        const schema = normalize(this.documentSchema?.schema)

        // check for errors in data
        const errors = validate(
            this.data,
            {
                type: 'form',
                items: schema['layouts']['full']
            }
        )

        // if we have any errors, stop and throw error back to view
        if (hasErrors(errors)) {
            this.errors = errors

            throw new Error(`${this.errorsCount} errors. Please fill out all *required fields before saving.`)
        }

    }

    get name(): string {
        return this._name || this.document?.name
    }

    setName = (value: string) => {
        this._name = value
    }

    notify = (isUrgent: boolean): void => {
        this.api.Routes.Document.postNotify(this.documentId, isUrgent ?? false)
    }

    get errorsCount(): number {
        return countErrors(this.errors)
    }
}

decorate(injectable(), DocumentEditController)
decorate(inject(DocumentStore), DocumentEditController, 0)
decorate(inject(DocumentSchemaStore), DocumentEditController, 1)
decorate(inject(DocumentSchemaRevisionStore), DocumentEditController, 2)
decorate(inject(Api), DocumentEditController, 3)
decorate(inject(MediaUtils), DocumentEditController, 4)

export { DocumentEditController, EverdocMediaUplaodErrorEdit }
